import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { OrgLocation } from '@/auth/auth-api';

/**
 * GET /api/Organization/{organizationId}/Locations
 *
 * Returns the practice locations the user has access to. Drives the
 * per-location filter buttons in the calendar sub-toolbar. Mirrors
 * Flutter's `parseLoggedUserData` post-login fetch — Flutter calls this
 * separately, then stitches the result onto `loggedData.organizationLocations`.
 * We keep it as its own React Query so the auth flow stays lean.
 *
 * Backend shape per `ApptLocations`:
 *   { id, name, imageUrl?, isArchived?, description? }
 *
 * Filters out archived locations so they don't render as live buttons.
 */
export function useOrgLocations(organizationId: string | null | undefined) {
  return useQuery({
    queryKey: ['org-locations', organizationId],
    queryFn: async ({ signal }) => {
      const list = await get<OrgLocation[]>(
        `/api/Organization/${organizationId}/Locations`,
        { signal },
      );
      return (list ?? []).filter(
        (loc) => !(loc as OrgLocation & { isArchived?: boolean }).isArchived,
      );
    },
    enabled: !!organizationId,
    staleTime: 1000 * 60 * 30, // org locations rarely change
  });
}
